import { Col, DatePicker, Form, Input, InputNumber, Row, Select, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { CountryCodes } from "~enums/countryCodes";
import { useParams } from "react-router-dom";
import { Sports } from "~enums/sports";
import { Genders } from "~enums/genders";

const STATUS_LIST = ["ACTIVE", "INACTIVE", "PENDING", "DECEASED", "UNREGISTERED"];
const GENDER_LIST = [Genders.Male, Genders.Female, Genders.Unknown];

const AddPersonForm = ({handleSetFormRef, data = {}, quickLink = false}) => {
  const {formatMessage} = useIntl();
  const {sport: sportFromParams} = useParams();
  const [form] = Form.useForm();
  const initValues = {
    ...data,
    dob: data?.dob ? dayjs(data?.dob, "YYYY-MM-DD") : null,
  };

  const countries = Object.values(CountryCodes).map(code => ({
    label: formatMessage({id: `countryCode.${code}`}),
    value: code
  }));

  useEffect(() => {
    handleSetFormRef(form);
  }, [form, handleSetFormRef]);

  return (
    <>
      <Form
        form={form}
        labelCol={{
          span: 10,
        }}
        wrapperCol={{
          span: 14,
        }}
        colon={false}
        labelWrap={true}
        initialValues={initValues}
      >
        <Row>
          <Col span={8}>
            <Form.Item
              name={"nameFullLocal"}
              label={formatMessage({
                id: "name.local.full",
                defaultMessage: "Local Full Name",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.local.full.name.item",
                  defaultMessage: "The full name of the {item} in the local Language",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
              required={true}
              rules={[
                {
                  required: true,
                  message: "Please input Local Full Name",
                },
              ]}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"nameFullLatin"}
              label={formatMessage({
                id: "name.latin.full",
                defaultMessage: "Latin Full Name",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.latin.full.name.item",
                  defaultMessage: "The full name of the {item} in latin characters",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"nameAbbreviated"}
              label={formatMessage({
                id: "abbreviated.name",
                defaultMessage: "Abbreviated Name",
              })}
              tooltip={formatMessage({
                id: "form.help.person.abbreviated.name",
                defaultMessage: "An abbreviated name for a person",
              })}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"dob"}
              label={formatMessage({
                id: "date.of.birth",
                defaultMessage: "Date Of Birth",
              })}
              tooltip={formatMessage({
                id: "form.help.person.dob",
                defaultMessage: "Person's Date of Birth",
              })}
            >
              <DatePicker style={{width: "100%"}} format={"DD/MM/YYYY"}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"nationality"}
              label={formatMessage({
                id: "nationality",
                defaultMessage: "Select a nationality",
              })}
              tooltip={formatMessage({
                id: "form.help.person.nationality",
                defaultMessage: "What is this person's nationality?",
              })}
              required={!quickLink}
              rules={[
                {
                  required: !quickLink,
                  message: "Please input Nationality",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={countries}
                onChange={(value) => {
                  if (sportFromParams === Sports.Hockey) {
                    const additionalDetails = form.getFieldValue("additionalDetails") ?? {};
                    form.setFieldsValue({additionalDetails : {...additionalDetails, representation: value}})
                  }
                }}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"gender"}
              label={formatMessage({
                id: "gender",
                defaultMessage: "Gender",
              })}
              tooltip={formatMessage({
                id: "form.help.person.gender",
                defaultMessage: "The gender of the person",
              })}
              required={true}
              rules={[
                {
                  required: true,
                  message: "Please input Gender",
                },
              ]}
            >
              <Select>
                {GENDER_LIST?.map((gender) => (
                  <Select.Option key={gender} value={gender}>
                    {gender}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"status"}
              label={formatMessage({
                id: "status",
                defaultMessage: "Status",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.status.item",
                  defaultMessage: "What is the current status of this {item}?",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
              required={true}
              rules={[
                {
                  required: true,
                  message: "Please input Status",
                },
              ]}
            >
              <Select>
                {STATUS_LIST?.map((status) => (
                  <Select.Option key={status} value={status}>
                    {status}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Typography.Title level={5}>Additional Details</Typography.Title>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "height"]}
              label={formatMessage({
                id: "height",
                defaultMessage: "Height (cm)",
              })}
              tooltip={formatMessage({
                id: "form.help.person.height",
                defaultMessage: "Height in cm's",
              })}
            >
              <InputNumber style={{width: "100%"}}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "weight"]}
              label={formatMessage({
                id: "weight",
                defaultMessage: "Weight (kg)",
              })}
              tooltip={formatMessage({
                id: "form.help.person.weight",
                defaultMessage: "Weight in kg's",
              })}
            >
              <InputNumber style={{width: "100%"}}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "heightImperial"]}
              rules={[
                {
                  pattern: /^([4-7])'([0-9]|1[0-1])$/,
                  message: formatMessage({
                    id: "validation.invalid.heightImperial",
                  }),
                },
              ]}
              label={formatMessage({
                id: "heightImperial",
              })}
              tooltip={formatMessage({
                id: "form.help.person.heightImperial",
              })}
              normalize={(value) => (value ? value : null)}
            >
              <Input style={{width: "100%"}}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "weightImperial"]}
              label={formatMessage({
                id: "weightImperial",
              })}
              tooltip={formatMessage({
                id: "form.help.person.weightImperial",
              })}
            >
              <InputNumber style={{width: "100%"}}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "representation"]}
              label={formatMessage({
                id: "representation",
              })}
              tooltip={formatMessage({
                id: "form.help.person.representing",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "juniorAssociationLeague"]}
              label={formatMessage({
                id: "juniorAssociationLeague",
              })}
              tooltip={formatMessage({
                id: "form.help.person.juniorAssociationLeague",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "homeTown"]}
              label={formatMessage({
                id: "homeTown",
              })}
              tooltip={formatMessage({
                id: "form.help.person.homeTown",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "college"]}
              label={formatMessage({
                id: "college",
              })}
              tooltip={formatMessage({
                id: "form.help.person.college",
              })}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "collegeClass"]}
              label={formatMessage({
                id: "collegeClass",
              })}
              tooltip={formatMessage({
                id: "form.help.person.collegeClass",
              })}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "school"]}
              label={formatMessage({
                id: "school",
              })}
              tooltip={formatMessage({
                id: "form.help.person.school",
              })}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["additionalDetails", "schoolClass"]}
              label={formatMessage({
                id: "schoolClass",
              })}
              tooltip={formatMessage({
                id: "form.help.person.schoolClass",
              })}
            >
              <Input style={{width: "100%"}}/>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Typography.Title level={5}>Social</Typography.Title>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["social", "website"]}
              label={formatMessage({
                id: "website",
                defaultMessage: "Website",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.item.website",
                  defaultMessage: "{item} Website",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["social", "facebook"]}
              label={formatMessage({
                id: "facebook",
                defaultMessage: "Facebook",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.item.facebook",
                  defaultMessage: "{item} Facebook",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["social", "twitter"]}
              label={formatMessage({
                id: "twitter",
                defaultMessage: "Twitter",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.item.twitter",
                  defaultMessage: "{item} Twitter",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["social", "wikipedia"]}
              label={formatMessage({
                id: "wikipedia",
                defaultMessage: "Wikipedia",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.item.wikipedia",
                  defaultMessage: "{item} Wikipedia Page",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["social", "instagram"]}
              label={formatMessage({
                id: "instagram",
                defaultMessage: "Instagram",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.item.instagram",
                  defaultMessage: "{item} Instagram",
                },
                {
                  item: formatMessage({
                    id: "person",
                    defaultMessage: "Person",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default AddPersonForm;
